type YTPlayer = {
  playVideo: () => void
  pauseVideo: () => void
  mute: () => void
  unMute: () => void
  isMuted: () => boolean
  getVolume: () => number
  setVolume: (volume: number) => void
  seekTo: (seconds: number, allowSeekAhead: boolean) => void
  loadVideoById: (videoId: string | { videoId: string; startSeconds?: number }) => void
  getCurrentTime: () => number
  getDuration: () => number
  getIframe: () => HTMLIFrameElement
  getPlayerState: () => number
  destroy: () => void
}

type YTNamespace = {
  Player: new (
    element: HTMLElement | string,
    config: {
      videoId?: string
      width?: string | number
      height?: string | number
      playerVars?: Record<string, string | number>
      events?: {
        onReady?: (event: { target: YTPlayer }) => void
        onStateChange?: (event: { data: number; target: YTPlayer }) => void
        onAutoplayBlocked?: () => void
        onError?: () => void
      }
    },
  ) => YTPlayer
  PlayerState: {
    PLAYING: number
    PAUSED: number
    ENDED: number
  }
}

declare global {
  interface Window {
    YT?: YTNamespace
    onYouTubeIframeAPIReady?: () => void
  }
}

let loadPromise: Promise<YTNamespace> | null = null

export function loadYoutubeIframeApi(): Promise<YTNamespace> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('YouTube API yalnızca tarayıcıda'))
  }
  if (window.YT?.Player) return Promise.resolve(window.YT)

  if (!loadPromise) {
    loadPromise = new Promise((resolve, reject) => {
      if (window.YT?.Player) {
        resolve(window.YT)
        return
      }

      const finish = () => {
        if (window.YT?.Player) resolve(window.YT)
      }

      const existing = document.querySelector('script[data-yt-iframe-api]')
      if (!existing) {
        const tag = document.createElement('script')
        tag.src = 'https://www.youtube.com/iframe_api'
        tag.async = true
        tag.dataset.ytIframeApi = '1'
        document.head.appendChild(tag)
      }

      const prior = window.onYouTubeIframeAPIReady
      window.onYouTubeIframeAPIReady = () => {
        prior?.()
        finish()
      }

      const poll = window.setInterval(() => {
        if (window.YT?.Player) {
          window.clearInterval(poll)
          resolve(window.YT)
        }
      }, 50)

      window.setTimeout(() => {
        window.clearInterval(poll)
        if (window.YT?.Player) resolve(window.YT)
        else reject(new Error('YouTube API yüklenemedi'))
      }, 12000)
    })
  }

  return loadPromise
}

export type { YTPlayer, YTNamespace }
